import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Camera, Check, Loader2, MapPin, Sparkles, Trash2, X,
} from 'lucide-react';

import { cn } from '@/lib/cn';
import { useCreateTastingEvent, useTastingEvents, type TastingEvent } from './useEvents';
import {
  useExtractWineFromPhotos, useSavePhotoTasting, type ExtractedWine,
} from './usePhotoTasting';
import { usePhotoStagingStore } from './photoStagingStore';

type Decision = 'cellar' | 'tasted_only' | 'both';
const today = () => new Date().toISOString().slice(0, 10);

const STORAGE_KEY = 'vinacora.current_tasting_event';

export function PhotoTastingPage() {
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const extract = useExtractWineFromPhotos();
  const save = useSavePhotoTasting();
  const createEvent = useCreateTastingEvent();
  const { data: todayEvents } = useTastingEvents({ date: today() });
  const setStaging = usePhotoStagingStore((s) => s.setStaging);

  const [photos, setPhotos] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [extracted, setExtracted] = useState<ExtractedWine | null>(null);
  const [decision, setDecision] = useState<Decision>('tasted_only');
  const [score, setScore] = useState<string>('');
  const [eventId, setEventId] = useState<number | null>(null);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventLocation, setNewEventLocation] = useState('');

  // Restaurar evento activo del día desde localStorage.
  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as { id: number; date: string };
        if (parsed.date === today()) {
          setEventId(parsed.id);
          return;
        }
      } catch { /* ignore */ }
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const onFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    const merged = [...photos, ...files].slice(0, 5);
    setPhotos(merged);
    setPreviews(merged.map((f) => URL.createObjectURL(f)));
  };

  const removePhoto = (i: number) => {
    const next = photos.filter((_, idx) => idx !== i);
    setPhotos(next);
    setPreviews(next.map((f) => URL.createObjectURL(f)));
  };

  const onExtract = async () => {
    if (!photos.length) return;
    try {
      const data = await extract.mutateAsync(photos);
      setExtracted(data);
    } catch (e: any) {
      alert('No se pudo analizar la etiqueta: ' + (e?.response?.data?.message ?? e?.message));
    }
  };

  const updateField = (key: keyof ExtractedWine, value: any) =>
    setExtracted((w) => (w ? { ...w, [key]: value } : w));

  const setCsv = (key: 'grape_varieties' | 'producers' | 'enologists' | 'agronomos', text: string) => {
    const arr = text
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    updateField(key, arr);
  };

  const pickEvent = (ev: TastingEvent) => {
    setEventId(ev.id);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: ev.id, date: today() }));
  };

  const createNewEvent = async () => {
    if (!newEventName.trim()) return;
    const ev = await createEvent.mutateAsync({
      name: newEventName.trim(),
      location: newEventLocation.trim() || null,
      event_date: today(),
      notes: null,
    });
    pickEvent(ev);
    setShowNewEvent(false);
    setNewEventName('');
    setNewEventLocation('');
  };

  const onSave = async () => {
    if (!extracted?.name) {
      alert('Faltan datos mínimos: el vino necesita un nombre.');
      return;
    }

    // Si el vino se va a sumar a la cava (o "ambos"), mandamos al usuario al
    // formulario completo de WineFormPage con los datos pre-cargados, fotos en
    // memoria y la opción de registrar la compra inicial.
    if (decision === 'cellar' || decision === 'both') {
      setStaging({
        photos,
        extracted,
        decision,
        score: score === '' ? null : Number(score),
        eventId: decision === 'both' ? eventId : null,
      });
      navigate('/wines/new?from_photo=1');
      return;
    }

    // Para "solo probado", guardamos el flow rápido como hasta ahora.
    try {
      await save.mutateAsync({
        photos,
        wine: {
          name: extracted.name,
          winery_name: extracted.winery,
          vintage: extracted.vintage,
          type: extracted.type,
          classification: extracted.classification,
          alcohol: extracted.alcohol,
          ph: extracted.ph,
          inv_number: extracted.inv_number,
          format: extracted.format ?? '750ml',
          bottle_shape: extracted.bottle_shape,
          notes: extracted.notes,
          grape_varieties: extracted.grape_varieties,
          producers: extracted.producers,
          enologists: [...(extracted.enologists ?? []), ...(extracted.agronomos ?? [])],
          place_path: extracted.place,
        },
        decision,
        score: score === '' ? null : Number(score),
        tasted_on: today(),
        tasting_event_id: eventId,
      });
      navigate('/tastings');
    } catch (e: any) {
      alert('No se pudo guardar: ' + (e?.response?.data?.message ?? e?.message));
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-2xl">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="text-sm text-notion-500 inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={14} /> Volver
      </button>

      <header className="mb-4">
        <h1 className="text-xl md:text-2xl font-medium flex items-center gap-2">
          <Camera size={20} className="text-notion-800" /> Probar vino
        </h1>
        <p className="text-sm text-notion-500">
          Sacá una o varias fotos del frente y dorso. La IA va a leer los datos y los
          completás vos en pantalla.
        </p>
      </header>

      {/* PASO 1: fotos */}
      <section className="card p-4 mb-4 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-medium text-notion-800">1. Fotos de la etiqueta</h2>
          <span className="text-xs text-notion-500">{photos.length}/5</span>
        </div>

        {previews.length > 0 && (
          <div className="grid grid-cols-3 gap-2">
            {previews.map((src, i) => (
              <div key={i} className="relative aspect-square rounded border border-notion-200 overflow-hidden">
                <img src={src} alt="" className="w-full h-full object-cover" />
                <button
                  type="button"
                  onClick={() => removePhoto(i)}
                  className="absolute top-1 right-1 bg-white/90 p-1 rounded text-notion-500 hover:text-red-600"
                  aria-label="Quitar"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={photos.length >= 5}
          className="btn-secondary w-full"
        >
          <Camera size={14} />
          {photos.length === 0 ? 'Sacar / elegir fotos' : 'Agregar más'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={onFilesPicked}
        />

        {!extracted && photos.length > 0 && (
          <button
            type="button"
            onClick={onExtract}
            disabled={extract.isPending}
            className="btn-primary w-full"
          >
            {extract.isPending ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Analizando con IA…
              </>
            ) : (
              <>
                <Sparkles size={14} /> Analizar etiqueta
              </>
            )}
          </button>
        )}
      </section>

      {/* PASO 2: datos extraídos editables */}
      {extracted && (
        <section className="card p-4 mb-4 space-y-3">
          <h2 className="font-medium text-notion-800">2. Datos extraídos · editá lo que necesites</h2>

          <Field label="Nombre" value={extracted.name ?? ''} onChange={(v) => updateField('name', v)} />
          <Field label="Bodega" value={extracted.winery ?? ''} onChange={(v) => updateField('winery', v)} />

          <div className="grid grid-cols-3 gap-2">
            <Field
              label="Añada"
              type="number"
              value={extracted.vintage?.toString() ?? ''}
              onChange={(v) => updateField('vintage', v ? Number(v) : undefined)}
            />
            <SelectField
              label="Tipo"
              value={extracted.type ?? ''}
              onChange={(v) => updateField('type', v)}
              options={[
                { value: '', label: '—' },
                { value: 'tinto', label: 'Tinto' },
                { value: 'blanco', label: 'Blanco' },
                { value: 'rosado', label: 'Rosado' },
                { value: 'espumante', label: 'Espumante' },
                { value: 'naranjo', label: 'Naranjo' },
                { value: 'dulce', label: 'Dulce' },
                { value: 'fortificado', label: 'Fortificado' },
              ]}
            />
            <Field
              label="Alcohol %"
              type="number"
              step="0.1"
              value={extracted.alcohol?.toString() ?? ''}
              onChange={(v) => updateField('alcohol', v ? Number(v) : undefined)}
            />
          </div>

          <Field
            label="Cepas (separadas por coma)"
            value={(extracted.grape_varieties ?? []).join(', ')}
            onChange={(v) => setCsv('grape_varieties', v)}
            placeholder="Malbec, Cabernet Franc"
          />
          <Field
            label="Productores"
            value={(extracted.producers ?? []).join(', ')}
            onChange={(v) => setCsv('producers', v)}
          />
          <Field
            label="Enólogos"
            value={(extracted.enologists ?? []).join(', ')}
            onChange={(v) => setCsv('enologists', v)}
          />
          <Field
            label="Ingenieros agrónomos"
            value={(extracted.agronomos ?? []).join(', ')}
            onChange={(v) => setCsv('agronomos', v)}
          />
          <Field
            label="Procedencia"
            value={extracted.place ?? ''}
            onChange={(v) => updateField('place', v)}
            placeholder="Argentina > Mendoza > Valle de Uco > Gualtallary"
          />

          <div className="grid grid-cols-2 gap-2">
            <Field
              label="Formato"
              value={extracted.format ?? '750ml'}
              onChange={(v) => updateField('format', v)}
            />
            <Field
              label="PH"
              type="number"
              step="0.01"
              value={extracted.ph?.toString() ?? ''}
              onChange={(v) => updateField('ph', v ? Number(v) : undefined)}
            />
          </div>

          <div>
            <label className="label">Notas</label>
            <textarea
              className="input"
              rows={3}
              value={extracted.notes ?? ''}
              onChange={(e) => updateField('notes', e.target.value)}
            />
          </div>
        </section>
      )}

      {/* PASO 3: decisión */}
      {extracted && (
        <section className="card p-4 mb-4 space-y-3">
          <h2 className="font-medium text-notion-800">3. ¿Qué hacés con este vino?</h2>
          <div className="grid grid-cols-1 gap-2">
            <DecisionOption
              active={decision === 'tasted_only'}
              onClick={() => setDecision('tasted_only')}
              title="Solo lo probé"
              subtitle="No entra a la cava. Queda en mi historial de catas."
            />
            <DecisionOption
              active={decision === 'cellar'}
              onClick={() => setDecision('cellar')}
              title="Lo agrego a la cava"
              subtitle="Aparece en Mis vinos. (Sin nota de cata por ahora.)"
            />
            <DecisionOption
              active={decision === 'both'}
              onClick={() => setDecision('both')}
              title="Ambos"
              subtitle="Lo sumo a la cava y registro la cata de hoy."
            />
          </div>

          {decision !== 'cellar' && (
            <>
              <div>
                <label className="label">Puntaje (opcional, 0-100)</label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  className="input"
                  value={score}
                  onChange={(e) => setScore(e.target.value)}
                />
              </div>

              <EventPicker
                todayEvents={todayEvents ?? []}
                eventId={eventId}
                onPick={pickEvent}
                onClear={() => {
                  setEventId(null);
                  localStorage.removeItem(STORAGE_KEY);
                }}
                onCreateClick={() => setShowNewEvent(true)}
              />

              {showNewEvent && (
                <div className="border border-notion-200 rounded p-3 space-y-2">
                  <p className="text-xs font-medium text-notion-700">Nuevo evento</p>
                  <input
                    className="input"
                    placeholder="Nombre del evento"
                    value={newEventName}
                    onChange={(e) => setNewEventName(e.target.value)}
                  />
                  <input
                    className="input"
                    placeholder="Lugar (opcional)"
                    value={newEventLocation}
                    onChange={(e) => setNewEventLocation(e.target.value)}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-secondary flex-1"
                      onClick={() => setShowNewEvent(false)}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      className="btn-primary flex-1"
                      onClick={createNewEvent}
                      disabled={!newEventName.trim() || createEvent.isPending}
                    >
                      Crear
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* PASO 4: guardar */}
      {extracted && (
        <div className="flex gap-2 pb-6">
          <button type="button" className="btn-secondary flex-1" onClick={() => navigate(-1)}>
            <X size={14} /> Cancelar
          </button>
          <button
            type="button"
            className="btn-primary flex-1"
            onClick={onSave}
            disabled={save.isPending}
          >
            <Check size={14} /> {save.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      )}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  step,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  step?: string;
  placeholder?: string;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type={type}
        step={step}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
    </div>
  );
}

function SelectField({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function DecisionOption({
  active, onClick, title, subtitle,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'text-left p-3 rounded border transition',
        active
          ? 'border-notion-800 bg-notion-50'
          : 'border-notion-200 hover:border-notion-400',
      )}
    >
      <p className="text-sm font-medium text-notion-800">{title}</p>
      <p className="text-xs text-notion-500">{subtitle}</p>
    </button>
  );
}

function EventPicker({
  todayEvents,
  eventId,
  onPick,
  onClear,
  onCreateClick,
}: {
  todayEvents: TastingEvent[];
  eventId: number | null;
  onPick: (ev: TastingEvent) => void;
  onClear: () => void;
  onCreateClick: () => void;
}) {
  const current = todayEvents.find((e) => e.id === eventId);

  return (
    <div>
      <label className="label flex items-center gap-2">
        <MapPin size={12} /> Evento (opcional)
      </label>
      {current ? (
        <div className="flex items-center justify-between bg-notion-50 border border-notion-200 rounded p-2">
          <div>
            <p className="text-sm font-medium">{current.name}</p>
            {current.location && (
              <p className="text-xs text-notion-500">{current.location}</p>
            )}
          </div>
          <button type="button" onClick={onClear} className="text-notion-400 hover:text-notion-800">
            <X size={14} />
          </button>
        </div>
      ) : (
        <>
          {todayEvents.length > 0 && (
            <div className="space-y-1 mb-2">
              {todayEvents.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => onPick(e)}
                  className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-notion-100"
                >
                  {e.name}
                  {e.location && <span className="text-notion-500 ml-2">· {e.location}</span>}
                </button>
              ))}
            </div>
          )}
          <button type="button" onClick={onCreateClick} className="btn-secondary w-full text-xs">
            + Nuevo evento
          </button>
        </>
      )}
    </div>
  );
}
